import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const CartContext = createContext(null);

const STORAGE_KEY = "rs_cart_v1";
const CART_ID_KEY = "rs_cart_id";

// null/undefined inventory_count = untracked (unlimited); digital items never sell out
export const stockLimit = (p) =>
  p?.product_type === "ai_companion" || p?.inventory_count === null || p?.inventory_count === undefined
    ? Infinity
    : Math.max(0, p.inventory_count);

export const isOutOfStock = (p) =>
  p?.product_type !== "ai_companion" && (p?.in_stock === false || stockLimit(p) <= 0);

export const getCartId = () => {
  let id = localStorage.getItem(CART_ID_KEY);
  if (!id) {
    id = `cart_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(CART_ID_KEY, id);
  }
  return id;
};

export const resetCartId = () => localStorage.removeItem(CART_ID_KEY);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Track the cart server-side for abandoned-cart recovery (debounced)
  const firstTrack = useRef(true);
  useEffect(() => {
    if (firstTrack.current && items.length === 0) {
      firstTrack.current = false;
      return;
    }
    firstTrack.current = false;
    const t = setTimeout(() => {
      api
        .post("/cart/track", {
          cart_id: getCartId(),
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        })
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [items]);

  const addItem = (product, qty = 1) => {
    if (isOutOfStock(product)) {
      toast.error(`${product.name} is out of stock.`);
      return;
    }
    const limit = stockLimit(product);
    const existing = items.find((i) => i.product_id === product.product_id);
    const current = existing ? existing.quantity : 0;
    const next = Math.min(current + qty, limit);
    if (next === current) {
      toast.warning(`Only ${limit} of ${product.name} available — already in your bag.`);
      setOpen(true);
      return;
    }
    if (next < current + qty) {
      toast.warning(`Only ${limit} of ${product.name} available.`);
    }
    setItems((prev) => {
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.product_id
            ? { ...i, quantity: next, inventory_count: product.inventory_count ?? null }
            : i,
        );
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          name: product.name,
          price: product.price,
          image: product.image,
          slug: product.slug,
          quantity: next,
          inventory_count: product.inventory_count ?? null,
          product_type: product.product_type || null,
        },
      ];
    });
    setOpen(true);
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) return removeItem(productId);
    const item = items.find((i) => i.product_id === productId);
    const limit = item ? stockLimit(item) : Infinity;
    const next = Math.min(qty, limit);
    if (next < qty) toast.warning(`Only ${limit} available.`);
    setItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity: next } : i)),
    );
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items],
  );
  const count = useMemo(
    () => items.reduce((s, i) => s + i.quantity, 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, subtotal, count, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
